import { useCallback, useMemo } from 'react';
import { useOptionalLanguage } from './useLanguage';
import { useContentOverrides } from './useContentOverrides';
import { translations, BASE_LANGUAGE, TranslationKey } from '@/lib/translations';

/** Interpolate `{token}` placeholders.
 *
 * Uses a replacer FUNCTION rather than a string: with a string replacement,
 * `$&`, `` $` ``, `$'` and `$n` inside the VALUE are expanded by
 * String.replace — so a user typing `` $` `` into a search box rendered
 * duplicated garbage in the empty-state copy. A function replacement disables
 * that expansion, and this also replaces every occurrence, not just the first.
 */
export function interpolate(
  template: string,
  vars: Record<string, string | number>,
): string {
  return template.replace(/\{(\w+)\}/g, (match, token: string) =>
    token in vars ? String(vars[token]) : match,
  );
}

/**
 * Resolve a key to its template, most specific source first:
 *
 *   admin override for this language
 *     -> shipped bundle for this language
 *     -> shipped base bundle (en-UG, the only guaranteed-complete one)
 *     -> the key itself, so a missing string is visible rather than blank.
 *
 * An override for the base language does NOT leak into other languages: an
 * admin editing English copy must not silently overwrite the French UI with
 * English text.
 */
export function resolveTemplate(
  key: string,
  language: string,
  overrides: Record<string, Record<string, string>>,
): string {
  return (
    overrides[language]?.[key] ??
    translations[language as keyof typeof translations]?.[key] ??
    overrides[BASE_LANGUAGE]?.[key] ??
    translations[BASE_LANGUAGE][key] ??
    key
  );
}

export function resolveCountTemplate(
  key: string,
  count: number,
  language: string,
  overrides: Record<string, Record<string, string>>,
  pluralRules = new Intl.PluralRules(language),
): string {
  const pluralKey = `${key}_${pluralRules.select(count)}`;
  const hasPluralTemplate =
    overrides[language]?.[pluralKey] !== undefined ||
    translations[language as keyof typeof translations]?.[pluralKey] !== undefined ||
    overrides[BASE_LANGUAGE]?.[pluralKey] !== undefined ||
    translations[BASE_LANGUAGE][pluralKey] !== undefined;
  return resolveTemplate(hasPluralTemplate ? pluralKey : key, language, overrides);
}

export function formatNumericTranslationVars(
  vars: Record<string, string | number>,
  numberFormat: Intl.NumberFormat,
): Record<string, string | number> {
  return Object.fromEntries(
    Object.entries(vars).map(([name, value]) => [
      name,
      typeof value === 'number' ? numberFormat.format(value) : value,
    ]),
  );
}

export function useTranslation() {
  const language = useOptionalLanguage()?.language ?? BASE_LANGUAGE;
  const { overrides, tokens } = useContentOverrides();
  const numberFormat = useMemo(() => new Intl.NumberFormat(language), [language]);
  const pluralRules = useMemo(() => new Intl.PluralRules(language), [language]);

  const t = useCallback(
    (key: TranslationKey, vars?: Record<string, string | number>): string => {
      const template = resolveTemplate(key as string, language, overrides);
      // Country/brand tokens are always available (src/lib/contentTokens.ts):
      // copy carries `{brand}` / `{country}` / `{supportPhone}` rather than a
      // hard-coded name, and caller-supplied vars win over the defaults.
      return interpolate(template, { ...tokens, ...(vars ?? {}) });
    },
    [language, overrides, tokens],
  );

  const formatNumber = useCallback(
    (value: number): string => numberFormat.format(value),
    [numberFormat],
  );

  const tCount = useCallback(
    (
      key: TranslationKey,
      count: number,
      vars?: Record<string, string | number>,
    ): string => {
      const template = resolveCountTemplate(key, count, language, overrides, pluralRules);
      const values = { ...(vars ?? {}), count };
      const formatted = formatNumericTranslationVars(values, numberFormat);
      return interpolate(template, { ...tokens, ...formatted });
    },
    [language, numberFormat, overrides, pluralRules, tokens],
  );

  return { t, tCount, formatNumber, language };
}
